import React, { useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';
import Fields from 'components/Fields';
import { FieldCurrency, Modal, Prompt } from 'components/Base';
import TableComponents from 'components/Table';
import { formatPercent, formatCurrency } from 'utils';
import Icons from 'components/Icons';
import ModelPortfolioAllocations from 'components/ModelPortfolio/ModelPortfolioAllocations';
import { TooltipIcon } from 'components/Base';
import classnames from 'classnames';
import UnsupportedFundDecision from './UnsupportedFundDecision';
import EditablePercent from 'components/Base/EditablePercent/EditablePercent';
import { getOverriddenCharges } from 'components/Policies/utils';
import { alterColor, secondaryText } from 'utils/colors';
import Checkbox from 'components/Checkbox/Checkbox';

const useStyles = makeStyles({
  footer: {
    backgroundColor: '#c6e0f4',
    lineHeight: 'unset'
  },
  icon: {
    fontSize: '1.5rem',
    cursor: 'pointer',
    transition: '0.3s'
  },
  boldRow: {
    fontWeight: 'bold',
    '& input': {
      fontWeight: 'bold'
    }
  },
  highlightedRow: {
    backgroundColor: '#fffcf5',
    cursor: 'pointer'
  },
  iconsBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '80px'
  },
  greyCell: {
    color: alterColor(secondaryText, -5)
  }
});

interface FundType {
  fund: any;
  meta?: any;
  fundId: number;
  removeFund?: (fundId: number) => any;
  allowPercentage: boolean;
  fundValues: any;
  percentageValue: string;
  subTotalValues: any;
  isWrapCashAlone: boolean;
  aggregateOcfChargeValue: string;
  aggregateFundChargeValue: string;
  aggregateTotalFundChargeValue: string;
  aggregateOneYPerformanceValue: number;
  aggregateThreeYPerformanceValue: number;
  aggregateFiveYPerformanceValue: number;
  formName: string;
  setIsManualFundModalOpen: ({
    isVisible,
    initialValues
  }: {
    isVisible: boolean;
    initialValues: any;
  }) => void;
  active?: boolean;
  removeInvalidFund: Function;
  showPerformance?: boolean;
  showAllocation?: boolean;
  withEmptyColumn?: boolean;
  isChecked?: boolean;
  onToggleCheck?: Function;
}

const Fund = ({
  fund,
  // eslint-disable-next-line
  removeFund = () => {},
  fundId,
  allowPercentage,
  fundValues,
  percentageValue,
  subTotalValues,
  isWrapCashAlone,
  aggregateOcfChargeValue,
  aggregateFundChargeValue,
  aggregateTotalFundChargeValue,
  aggregateOneYPerformanceValue,
  aggregateThreeYPerformanceValue,
  aggregateFiveYPerformanceValue,
  formName,
  // eslint-disable-next-line
  setModelPortfolioEditor = () => {},
  isIncomplete,
  setIsManualFundModalOpen,
  setReplicateFundFromInvalid,
  removeInvalidFund,
  showPerformance = true,
  showAllocation = true,
  withEmptyColumn = false,
  overrideDivideBy = 100,
  getAggregateValue = (value: any) => value,
  isChecked = false,
  // eslint-disable-next-line
  onToggleCheck = () => {},
}: any) => {
  const [isModelPortfolioExpanded, setModelPortfolioExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);
  const isModelPortfolio = fundValues.model_portfolio;
  const { read_only: isReadOnly, editable = true } = fundValues;

  const {
    ocf,
    active,
    trading_costs,
    one_y_performace,
    three_y_performace,
    five_y_performace,
    sector_name
    // isValid
  } = isModelPortfolio ? fundValues.model_portfolio : fundValues.fund;

  const hasMPInactiveFund = isModelPortfolio
    ? fundValues.model_portfolio.fund_allocations.some((fundDetails: any) => {
        return fundDetails.fund && !fundDetails.fund.active;
      })
    : false;

  const isFundActive = isModelPortfolio ? !hasMPInactiveFund : active;
  const isFundValid = isModelPortfolio
    ? true
    : typeof fundValues.fund.isValid === 'boolean'
      ? fundValues.fund.isValid
      : true;

  const { fundOcf, fundTradingCosts, customTotalFundCharge } =
    getOverriddenCharges(
      fundValues.fund_charge_overrides,
      fundValues.model_portfolio,
      ocf,
      trading_costs,
      overrideDivideBy
    );

  const isWrapCash = !isModelPortfolio && fundValues.fund.name === 'Wrap Cash';
  const classes = useStyles();
  const subTotalInPercentage =
    Number(subTotalValues.totalFundValue) !== 0
      ? (subTotalValues.fund_value / Number(subTotalValues.totalFundValue)) *
        100
      : 0;

  if (!('fund_value' in fundValues)) {
    fundValues.fund_value =
      Number(fundValues.allocation) * Number(subTotalValues.fund_value);
  }

  return (
    <>
      {isWrapCash && !isWrapCashAlone && (
        <TableRow>
          <TableComponents.TableCell
            style={{ width: '100px' }}
            className={classes.footer}
          ></TableComponents.TableCell>
          <TableComponents.TableCell
            width={isIncomplete ? '100px' : '200px'}
            className={classes.footer}
          >
            Sub total
          </TableComponents.TableCell>
          <TableComponents.TableCell className={classes.footer} />
          {showAllocation && (
            <TableComponents.TableCell className={classes.footer}>
              {formatCurrency(subTotalValues.fund_value)}
            </TableComponents.TableCell>
          )}
          <TableComponents.TableCell className={classes.footer}>
            {formatPercent(subTotalInPercentage)}
          </TableComponents.TableCell>
          <TableComponents.TableCell className={classes.footer}>
            {getAggregateValue(aggregateOcfChargeValue)}
          </TableComponents.TableCell>
          <TableComponents.TableCell className={classes.footer}>
            {getAggregateValue(aggregateFundChargeValue)}
          </TableComponents.TableCell>
          <TableComponents.TableCell className={classes.footer}>
            {getAggregateValue(aggregateTotalFundChargeValue)}
          </TableComponents.TableCell>
          {showPerformance && (
            <>
              <TableComponents.TableCell className={classes.footer}>
                {getAggregateValue(aggregateOneYPerformanceValue)}
              </TableComponents.TableCell>
              <TableComponents.TableCell className={classes.footer}>
                {getAggregateValue(aggregateThreeYPerformanceValue)}
              </TableComponents.TableCell>
              <TableComponents.TableCell className={classes.footer}>
                {getAggregateValue(aggregateFiveYPerformanceValue)}
              </TableComponents.TableCell>
            </>
          )}
          {withEmptyColumn && (
            <>
              <TableComponents.TableCell />
              <TableComponents.TableCell />
            </>
          )}
          <TableComponents.TableCell className={classes.footer} />
        </TableRow>
      )}
      <TableRow
        onClick={() => {
          if (!active) setIsModalOpen(true);
        }}
      >
        <TableComponents.TableCell style={{ width: '10px' }}>
          <Checkbox isChecked={!!isChecked} onClick={() => onToggleCheck()} />
        </TableComponents.TableCell>
        <TableComponents.TableCell
          className={classnames(
            isModelPortfolio && classes.boldRow,
            isModelPortfolio && !editable && classes.greyCell,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          {isModelPortfolio
            ? fundValues.model_portfolio.name
            : fundValues.fund.name}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="160px"
          className={classnames(
            isModelPortfolio && classes.boldRow,
            isModelPortfolio && !editable && classes.greyCell,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          {sector_name || '-'}
        </TableComponents.TableCell>
        {showAllocation && (
          <TableComponents.TableCell
            width="100px"
            className={classnames(
              isModelPortfolio && classes.boldRow,
              isModelPortfolio && !editable && classes.greyCell,
              !isModelPortfolio && !active && classes.highlightedRow
            )}
          >
            {editable ? (
              <FieldCurrency
                name={`${fund}.fund_value`}
                className={classnames(
                  isModelPortfolio && classes.boldRow,
                  isModelPortfolio && !editable && classes.greyCell,
                  !isModelPortfolio && !active && classes.highlightedRow
                )}
                fullWidth
                clearfieldinform={formName}
                type="string"
                component={Fields.Text}
                disabled={isReadOnly || allowPercentage}
              />
            ) : (
              <>{formatCurrency(fundValues.fund_value)}</>
            )}
          </TableComponents.TableCell>
        )}
        <TableComponents.TableCell
          width="80px"
          className={classnames(
            isModelPortfolio && classes.boldRow,
            isModelPortfolio && !editable && classes.greyCell,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          {percentageValue}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="80px"
          className={classnames(
            isModelPortfolio && classes.boldRow,
            isModelPortfolio && !editable && classes.greyCell,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          {isWrapCash ? (
            ''
          ) : (
            <>
              {isModelPortfolio ? (
                <>{formatPercent(Number(fundOcf) * 100)}</>
              ) : (
                <EditablePercent
                  name={`${fund}.fund_charge_overrides[0].ocf`}
                  formName={formName}
                  defaultValue={ocf}
                />
              )}
            </>
          )}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="70px"
          className={classnames(
            isModelPortfolio && classes.boldRow,
            isModelPortfolio && !editable && classes.greyCell,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          {isWrapCash ? (
            ''
          ) : (
            <>
              {isModelPortfolio ? (
                <>{formatPercent(Number(fundTradingCosts) * 100)}</>
              ) : (
                <EditablePercent
                  name={`${fund}.fund_charge_overrides[0].trading_costs`}
                  formName={formName}
                  defaultValue={trading_costs}
                />
              )}
            </>
          )}
        </TableComponents.TableCell>
        <TableComponents.TableCell
          width="70px"
          className={classnames(
            isModelPortfolio && classes.boldRow,
            isModelPortfolio && !editable && classes.greyCell,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          {isWrapCash ? '' : formatPercent(customTotalFundCharge * 100)}
        </TableComponents.TableCell>
        {showPerformance && (
          <>
            <TableComponents.TableCell
              width="80px"
              className={classnames(
                isModelPortfolio && classes.boldRow,
                isModelPortfolio && !editable && classes.greyCell,
                !isModelPortfolio && !active && classes.highlightedRow
              )}
            >
              {isWrapCash
                ? ''
                : one_y_performace
                  ? formatPercent(Number(one_y_performace) * 100, true)
                  : '-'}
            </TableComponents.TableCell>
            <TableComponents.TableCell
              width="80px"
              className={classnames(
                isModelPortfolio && classes.boldRow,
                isModelPortfolio && !editable && classes.greyCell,
                !isModelPortfolio && !active && classes.highlightedRow
              )}
            >
              {isWrapCash
                ? ''
                : three_y_performace
                  ? formatPercent(Number(three_y_performace) * 100, true)
                  : '-'}
            </TableComponents.TableCell>
            <TableComponents.TableCell
              width="80px"
              className={classnames(
                isModelPortfolio && classes.boldRow,
                isModelPortfolio && !editable && classes.greyCell,
                !isModelPortfolio && !active && classes.highlightedRow
              )}
            >
              {isWrapCash
                ? ''
                : five_y_performace
                  ? formatPercent(Number(five_y_performace) * 100, true)
                  : '-'}
            </TableComponents.TableCell>
          </>
        )}
        {withEmptyColumn && (
          <>
            <TableComponents.TableCell />
            <TableComponents.TableCell />
          </>
        )}
        <TableComponents.TableCell
          width="85px"
          className={classnames(
            isModelPortfolio && classes.boldRow,
            !isModelPortfolio && !active && classes.highlightedRow
          )}
        >
          <div
            className={classnames(
              classes.iconsBox,
              !isModelPortfolio && !active && classes.highlightedRow
            )}
          >
            {isModelPortfolio && (
              <>
                {fundValues.model_portfolio.type === 2 && (
                  <Icons.Edit
                    width="1.1rem"
                    onClick={() => {
                      setModelPortfolioEditor({
                        isOpen: true,
                        id: fundValues.model_portfolio.id,
                        funds: fundValues.model_portfolio.fund_allocations,
                        name: fundValues.model_portfolio.name,
                        index: fundId,
                        source: fundValues.read_only ? 1 : 2
                      });
                    }}
                    className={classes.icon}
                  />
                )}
                <ExpandMoreIcon
                  width="1.3rem"
                  className={classes.icon}
                  onClick={() =>
                    setModelPortfolioExpanded(!isModelPortfolioExpanded)
                  }
                  style={{
                    transform: isModelPortfolioExpanded
                      ? 'rotate(180deg)'
                      : 'rotate(0)'
                  }}
                />
              </>
            )}
            {!isFundValid && (
              <TooltipIcon
                text="Recreate fund manually"
                icon="edit"
                onClick={() =>
                  setIsManualFundModalOpen({
                    isVisible: true,
                    initialValues: isModelPortfolio
                      ? {}
                      : {
                          id: fundId,
                          name: fundValues.fund.name,
                          isin: fundValues.fund.isin,
                          ocf: formatPercent(fundValues.fund.ocf),
                          trading_costs: formatPercent(
                            fundValues.fund.trading_costs
                          ),
                          fund_value: fundValues.fund_value
                        }
                  })
                }
              />
            )}
            {editable && (
              <Icons.DeleteIcon
                width="1.5rem"
                style={{ minWidth: '1.5rem' }}
                onClick={() => setIsDeletePromptOpen(true)}
                className={classes.icon}
              />
            )}
            {(!isFundActive || !isFundValid) && (
              <TooltipIcon
                text={
                  !isFundValid
                    ? 'Fund is invalid'
                    : 'Fund is no longer supported.'
                }
                icon="warn"
              />
            )}
          </div>
        </TableComponents.TableCell>
      </TableRow>

      {isModelPortfolio && isModelPortfolioExpanded && (
        <ModelPortfolioAllocations
          id={fundValues.model_portfolio.id}
          fundAllocations={fundValues.model_portfolio.fund_allocations}
          overrides={fundValues.fund_charge_overrides}
          modelPortfolioFundValue={fundValues.fund_value}
          sourceValue={fundValues.read_only ? 1 : 2}
          baseName={fund}
          formName={formName}
          showAllocation={showAllocation}
          showPerformance={showPerformance}
          withEmptyColumn={withEmptyColumn}
          overrideDivideBy={overrideDivideBy}
          nested={!editable}
          subTotalValues={subTotalValues}
        />
      )}
      {(!isFundActive || !isFundValid) && (
        <Modal
          isOpen={isModalOpen}
          closeModalFunc={() => setIsModalOpen(false)}
        >
          <UnsupportedFundDecision
            close={() => setIsModalOpen(false)}
            setReplicateFundFromInvalid={setReplicateFundFromInvalid}
            fundName={
              !!fundValues.fund && !!fundValues.fund.name
                ? fundValues.fund.name
                : fundValues.model_portfolio.name
            }
            fundValue={fundValues.fund_value}
            oldFundId={
              !!fundValues.fund && !!fundValues.fund.id
                ? fundValues.fund.id
                : fundValues.fund && fundValues.fund.id === null
                  ? null
                  : fundValues.model_portfolio.id
            }
            removeInvalidFund={removeInvalidFund}
          />
        </Modal>
      )}
      <Prompt
        isOpen={isDeletePromptOpen}
        title={`Are you sure you want to remove this ${isModelPortfolio ? 'portfolio' : 'fund'}?`}
        confirmAction={() => {
          removeFund(fundId);
          setIsDeletePromptOpen(false);
        }}
        cancelAction={() => setIsDeletePromptOpen(false)}
        isProcessing={false}
      />
    </>
  );
};

export default Fund;
